"use client";

import type { ProcessedSet, Review } from "@workspace/types";
import { cn } from "@workspace/ui/lib/utils";
import {
	getSetButtonClassName,
	getSetButtonContent,
	getSetStatus,
} from "@/lib/utils/set-card-helpers";

interface ReadOnlyStatusDisplayProps {
	set: ProcessedSet;
	review: Review | undefined;
	layout?: "horizontal" | "vertical";
	className?: string;
}

/**
 * Non-interactive status display for read-only views
 * Shows the profile owner's status without any interactive elements
 */
export function ReadOnlyStatusDisplay({
	set,
	review,
	layout = "horizontal",
	className,
}: ReadOnlyStatusDisplayProps) {
	// Check if the profile owner has any interaction with this set
	const hasStatus =
		review?.wantToSee ||
		review?.mustSee ||
		review?.sawLive ||
		review?.sawStream ||
		(review?.rating && review.rating > 0) ||
		review?.loved ||
		(review?.content && review.content.trim().length > 0);

	if (!hasStatus) return null;

	const { status } = getSetStatus(review);

	return (
		<div
			className={cn(
				"flex items-center justify-center gap-1 rounded-lg text-xs sm:text-sm",
				layout === "horizontal"
					? "min-w-[4rem] flex-shrink px-1.5 py-1 sm:min-w-[5rem] sm:px-2"
					: "mt-3 w-full min-w-0 whitespace-nowrap rounded-md border py-1 text-center text-sm",
				// Remove hover and active states for read-only display
				getSetButtonClassName(status).replace(
					/hover:[^ ]+ |active:[^ ]+ /g,
					"",
				),
				className,
			)}
		>
			{getSetButtonContent(
				status,
				set,
				layout === "vertical" ? false : undefined,
			)}
		</div>
	);
}
